<template>
  <div class="address-form-fields">
    <!-- Country Field -->
    <div class="address-field" :class="countryFieldClass">
      <InfoContainer
        :message="countryErrorMessage"
        state="error"
      >
        <Label label="Country" :required="true">
          <SingleSelector
            v-model="addressData.country"
            :label="null"
            :disabled="disabled"
            filterable
            placeholder="Country"
            size="medium"
            :options="localCountryOptions"
            @change="handleCountryChange"
            @filter-change="filterCountryOptions"
          />
        </Label>
      </InfoContainer>
    </div>

    <!-- Street Address Field -->
    <div class="address-field" :class="streetFieldClass">
      <InfoContainer
        state="error"
      >
        <Label label="Street Address" :required="true">
          <div class="custom-address-input">
            <AddressAutofilledUnit
              :key="`addr-${addressData.country || 'none'}`"
              question="Street Address"
              :default="addressData.streetAddress"
              :preferCountryCode="addressData.country || null"
              :required="true"
              :hide-question="true"
              :displayMode="'street'"
              :disabled="disabled"
              @autofillAddress="updateStreetAddress"
              @handleValueChange="(value, key) => { updateAddressField(value, 'streetAddress'); $emit('address-edit'); }"
            />
          </div>
        </Label>
      </InfoContainer>
    </div>

    <!-- City, State/Province, Postal Code Row (3-column grid) -->
    <div class="flex w-full gap-3">
      <!-- City -->
      <div class="w-1/3">
        <InfoContainer
          :message="cityErrorMessage"
          state="error"
        >
          <Label label="City" :required="true">
            <TextInput
              v-model="addressData.city"
              :disabled="disabled"
              placeholder="City"
              size="medium"
              @input="$emit('address-edit')"
            />
          </Label>
        </InfoContainer>
      </div>

      <!-- State/Province -->
      <div class="w-1/3">
        <InfoContainer
          :message="stateErrorMessage"
          state="error"
        >
          <Label :label="stateLabel" :required="isStateRequired">
            <template v-if="addressData.country === 'US'">
              <SingleSelector
                v-model="addressData.state"
                :options="usStateOptions"
                :disabled="disabled"
                placeholder="Select State"
                size="medium"
                @change="$emit('address-edit')"
              />
            </template>
            <TextInput
              v-else
              v-model="addressData.state"
              :disabled="disabled"
              :placeholder="stateLabel"
              size="medium"
              @input="$emit('address-edit')"
            />
          </Label>
        </InfoContainer>
      </div>

      <!-- Postal Code -->
      <div class="w-1/3">
        <InfoContainer
          :message="zipcodeErrorMessage"
          state="error"
        >
          <Label label="Postal Code" :required="true">
            <TextInput
              v-model="addressData.zipcode"
              :disabled="disabled"
              placeholder="Postal Code"
              size="medium"
              @input="$emit('address-edit')"
            />
          </Label>
        </InfoContainer>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import AddressAutofilledUnit from '@/components/AddressForm/AddressAutofilledUnit.vue';
import { getCountryList } from "@/api/va-order-page/pricing/shipping";
import { getStateLabelByCountry, isStateRequired } from '@/utils/addressUtils';
import { filterCountry } from "@/utils/filterCountry.js";
import { getShippingAddressLengthErrors, formatShippingLengthMessage } from '@/utils/shippingAddressLimits';

export default {
  name: 'AddressFormFields',
  components: {
    AddressAutofilledUnit,
  },
  props: {
    modelValue: {
      type: Object,
      required: false,
      default: () => ({
        country: '',
        streetAddress: '',
        city: '',
        state: '',
        zipcode: ''
      })
    },
    showErrors: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showAddressEditIndicator: {
      type: Boolean,
      default: false
    },
    // Layout customization
    countryFieldClass: {
      type: String,
      default: ''
    },
    streetFieldClass: {
      type: String,
      default: ''
    },
    cityStateZipContainerClass: {
      type: String,
      default: 'flex gap-x-3'
    }
  },
  emits: ['update:modelValue', 'address-edit'],
  data() {
    return {
      addressData: { ...this.modelValue },
      isUpdatingFromProp: false,
      localCountryOptions: [],
      usStateOptions: [
        { value: 'AL', label: 'AL' }, { value: 'AK', label: 'AK' }, { value: 'AZ', label: 'AZ' }, { value: 'AR', label: 'AR' },
        { value: 'CA', label: 'CA' }, { value: 'CO', label: 'CO' }, { value: 'CT', label: 'CT' }, { value: 'DE', label: 'DE' },
        { value: 'FL', label: 'FL' }, { value: 'GA', label: 'GA' }, { value: 'HI', label: 'HI' }, { value: 'ID', label: 'ID' },
        { value: 'IL', label: 'IL' }, { value: 'IN', label: 'IN' }, { value: 'IA', label: 'IA' }, { value: 'KS', label: 'KS' },
        { value: 'KY', label: 'KY' }, { value: 'LA', label: 'LA' }, { value: 'ME', label: 'ME' }, { value: 'MD', label: 'MD' },
        { value: 'MA', label: 'MA' }, { value: 'MI', label: 'MI' }, { value: 'MN', label: 'MN' }, { value: 'MS', label: 'MS' },
        { value: 'MO', label: 'MO' }, { value: 'MT', label: 'MT' }, { value: 'NE', label: 'NE' }, { value: 'NV', label: 'NV' },
        { value: 'NH', label: 'NH' }, { value: 'NJ', label: 'NJ' }, { value: 'NM', label: 'NM' }, { value: 'NY', label: 'NY' },
        { value: 'NC', label: 'NC' }, { value: 'ND', label: 'ND' }, { value: 'OH', label: 'OH' }, { value: 'OK', label: 'OK' },
        { value: 'OR', label: 'OR' }, { value: 'PA', label: 'PA' }, { value: 'RI', label: 'RI' }, { value: 'SC', label: 'SC' },
        { value: 'SD', label: 'SD' }, { value: 'TN', label: 'TN' }, { value: 'TX', label: 'TX' }, { value: 'UT', label: 'UT' },
        { value: 'VT', label: 'VT' }, { value: 'VA', label: 'VA' }, { value: 'WA', label: 'WA' }, { value: 'WV', label: 'WV' },
        { value: 'WI', label: 'WI' }, { value: 'WY', label: 'WY' }
      ],
      countryOptions: []
    };
  },
  computed: {
    stateLabel() {
      return getStateLabelByCountry(this.addressData.country);
    },
    isStateRequired() {
      return isStateRequired(this.addressData.country);
    },
    countryErrorMessage() {
      if (this.showErrors && !this.addressData.country) {
        return 'Required fields cannot be empty.';
      }
      return '';
    },
    // VP-18765: { field: limit } for every field over the shipping backend's
    // length limit; shown as soon as the value is over, independent of
    // showErrors, and included in validate().
    lengthErrors() {
      return getShippingAddressLengthErrors(this.addressData);
    },
    streetErrorMessage() {
      if (this.lengthErrors.streetAddress) {
        return formatShippingLengthMessage('Street Address', this.lengthErrors.streetAddress);
      }
      if (this.showAddressEditIndicator) {
        return 'This address is invalid.';
      }
      if (this.showErrors && !this.addressData.streetAddress) {
        return 'Required fields cannot be empty.';
      }
      return '';
    },
    cityErrorMessage() {
      if (this.lengthErrors.city) {
        return formatShippingLengthMessage('City', this.lengthErrors.city);
      }
      if (this.showErrors && !this.addressData.city) {
        return 'Required fields cannot be empty.';
      }
      return '';
    },
    stateErrorMessage() {
      if (this.lengthErrors.state) {
        return formatShippingLengthMessage(this.stateLabel, this.lengthErrors.state);
      }
      if (this.showErrors && this.isStateRequired && !this.addressData.state) {
        return 'Required fields cannot be empty.';
      }
      return '';
    },
    zipcodeErrorMessage() {
      if (this.lengthErrors.zipcode) {
        return formatShippingLengthMessage('Zip Code', this.lengthErrors.zipcode);
      }
      if (this.showErrors && !this.addressData.zipcode) {
        return 'Required fields cannot be empty.';
      }
      return '';
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        // Set flag to prevent addressData watcher from emitting
        this.isUpdatingFromProp = true;
        this.addressData = { ...newVal };
        this.$nextTick(() => {
          this.isUpdatingFromProp = false;
        });
      },
      deep: true
    },
    addressData: {
      handler(newVal) {
        // Only emit if change didn't come from modelValue watcher
        if (!this.isUpdatingFromProp) {
          this.$emit('update:modelValue', { ...newVal });
        }
      },
      deep: true
    }
  },
  async mounted() {
    await this.loadCountryOptions();
  },
  methods: {
    async loadCountryOptions() {
      try {
        const res = await getCountryList();
        const data = res.data;
        data.forEach(country => {
          country.label = country.countryName + " (" + country.countryNameTwoBytes +")";
          country.value = country.countryNameTwoBytes;
        });
        this.countryOptions = data;
        this.localCountryOptions = this.sortByPriority(data);
      } catch (error) {
        console.error('Failed to load country options:', error);
      }
    },
    sortByPriority(countryList) {
      if (!countryList) return [];
      let prioritizedCountries = [
        "United States", "Canada", "United Kingdom (Great Britain)", "Australia",
        "Germany", "France", "Netherlands", "Spain", "Italy", "Sweden", "Switzerland"
      ];
      let priorityMap = new Map(prioritizedCountries.map((country, index) => [country, index]));
      let priorityList = countryList.filter(country => priorityMap.has(country.countryName))
                                    .sort((a, b) => priorityMap.get(a.countryName) - priorityMap.get(b.countryName));
      let otherList = countryList.filter(country => !priorityMap.has(country.countryName))
                                 .sort((a, b) => a.countryName.localeCompare(b.countryName));
      return [...priorityList, ...otherList];
    },
    filterCountryOptions(input) {
      this.localCountryOptions = filterCountry(input, this.sortByPriority(this.countryOptions));
    },
    handleCountryChange() {
      this.$emit('address-edit');
    },
    updateStreetAddress(selectedAddress) {
      let street = `${selectedAddress.street_number || ''} ${selectedAddress.route || ''}`.trim();
      if (selectedAddress.subpremise) {
        street = `${street} #${selectedAddress.subpremise}`.trim();
      }
      this.addressData.streetAddress = street;
      this.addressData.city = selectedAddress.locality || '';
      this.addressData.state = selectedAddress.administrative_area_level_1 || '';
      this.addressData.zipcode = selectedAddress.postal_code || '';
      this.addressData.country = selectedAddress.country || '';
      this.$emit('address-edit');
    },
    updateAddressField(value) {
      this.addressData.streetAddress = value;
    },
    // Validation method
    validate() {
      const errors = [];
      if (!this.addressData.country) errors.push('Country is required');
      if (!this.addressData.streetAddress) errors.push('Street Address is required');
      if (!this.addressData.city) errors.push('City is required');
      if (this.isStateRequired && !this.addressData.state) errors.push('State is required');
      if (!this.addressData.zipcode) errors.push('Postal Code is required');
      Object.entries(this.lengthErrors).forEach(([field, limit]) => {
        errors.push(`${field} must be ${limit} characters or fewer`);
      });
      return {
        valid: errors.length === 0,
        errors
      };
    }
  }
};
</script>

<style scoped>
.address-form-fields {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.address-field {
  margin-bottom: 0;
}

.custom-address-input {
  width: 100%;
}

.flex {
  display: flex;
}

.flex-1 {
  flex: 1;
}

.gap-x-3 {
  gap: 12px;
}

.w-full {
  width: 100%;
}
</style>
